
import React from 'react';
import Layout from '@/components/Layout';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const TermsOfUse = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Use | GetCalculator</title>
        <meta 
          name="description" 
          content="Terms of Use for GetCalculator - Read our terms and conditions for using our free online calculator tools and services."
        />
        <link rel="canonical" href="https://getcalculator.net/terms-of-use" />
        <meta property="og:title" content="Terms of Use | GetCalculator" />
        <meta 
          property="og:description" 
          content="Terms of Use for GetCalculator - Read our terms and conditions for using our free online calculator tools and services." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://getcalculator.net/terms-of-use" />
      </Helmet>
      <Layout showBackButton>
        <div className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-3xl font-bold mb-4">Terms of Use</h1>
              <p className="text-muted-foreground">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>

            <Card>
              <CardContent className="p-8 space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    By accessing and using GetCalculator ("we," "our," or "us"), you accept and agree to be bound by the terms and provision of this agreement. 
                    If you do not agree to abide by the above, please do not use this service.
                  </p>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    GetCalculator provides free online calculator tools for various purposes including but not limited to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Financial calculations and planning tools</li>
                    <li>Health and wellness calculators</li>
                    <li>Educational and mathematical tools</li>
                    <li>Lifestyle and everyday calculators</li>
                    <li>Professional and business tools</li>
                  </ul>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">3. Use License</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Permission is granted to temporarily use GetCalculator for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Modify or copy the materials</li>
                    <li>Use the materials for any commercial purpose or for any public display</li>
                    <li>Attempt to reverse engineer any software contained on the website</li>
                    <li>Remove any copyright or other proprietary notations from the materials</li>
                  </ul>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">4. Disclaimer</h2>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="text-yellow-800 dark:text-yellow-200 leading-relaxed">
                      <strong>Important:</strong> The calculators and tools provided on this website are for informational and educational purposes only. 
                      They should not be considered as professional financial, medical, legal, or other advice. The results are estimates based on the 
                      information you provide and should not be used as the sole basis for any decision-making.
                    </p>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mt-4">
                    The materials on GetCalculator are provided on an 'as is' basis. GetCalculator makes no warranties, expressed or implied, 
                    and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of 
                    merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                  </p>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">5. Accuracy of Materials</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    The materials appearing on GetCalculator could include technical, typographical, or photographic errors. GetCalculator does 
                    not warrant that any of the materials on its website are accurate, complete, or current. GetCalculator may make changes to 
                    the materials contained on its website at any time without notice. However, GetCalculator does not make any commitment to 
                    update the materials.
                  </p>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">6. User Responsibilities</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    When using our calculators and tools, you agree to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Provide accurate information to the best of your knowledge</li>
                    <li>Verify results with qualified professionals when making important decisions</li>
                    <li>Use the tools only for lawful purposes</li>
                    <li>Not attempt to interfere with the proper functioning of the website</li>
                    <li>Respect the intellectual property rights of GetCalculator</li>
                  </ul>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">7. Limitations</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    In no event shall GetCalculator or its suppliers be liable for any damages (including, without limitation, damages for loss of 
                    data or profit, or due to business interruption) arising out of the use or inability to use the materials on GetCalculator, 
                    even if GetCalculator or an authorized representative has been notified orally or in writing of the possibility of such damage. 
                    Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or 
                    incidental damages, these limitations may not apply to you.
                  </p>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">8. Privacy</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the website, 
                    to understand our practices regarding the collection and use of your information.
                  </p>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">9. Modifications</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    GetCalculator may revise these terms of use at any time without notice. By using this website, you are agreeing to be bound 
                    by the then current version of these terms of use.
                  </p>
                </section>

                <Separator />

                <section>
                  <h2 className="text-2xl font-semibold mb-4">10. Contact Information</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    If you have any questions about these Terms of Use, please contact us through the feedback form on our website or 
                    by visiting our contact page.
                  </p>
                </section>

                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
                    <strong>Note:</strong> These terms constitute the entire agreement between GetCalculator and you with respect to your use of the website. 
                    If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default TermsOfUse;
